import React, { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useMenuTriggerState, useTreeState } from "react-stately";
import {
  useButton,
  useMenuTrigger,
  useMenu,
  useMenuItem,
  mergeProps,
  FocusScope,
  useOverlay,
  DismissButton,
  useFocus,
  useKeyboard,
} from "react-aria";
import styled from "styled-components";
import { Button, Box } from "@bca/ui-library";
import { ColorProps, color } from "styled-system";

import TimesSVG from "../Icons/TimesSVG";
import PrintSVG from "./PrintSVG";
import { StoreType } from "../../redux/StoreType";
import { GET_APP_STATE } from "../../redux/slices/appSlice";

const PrintButton = styled(Button)<ColorProps>`
  ${(props) => props.css}
  ${color}
  div {
    min-width: 16px;
    min-height: 16px;
    max-width: 20px;
    max-height: 20px;
    width: 16px;
    height: 16px;
    margin-right: 0;
  }

  :hover {
    background-color: white;

    svg {
      fill: black;
    }
  }
`;

function PrintMenuButton(props) {
  // Create state based on the incoming props
  let state = useMenuTriggerState(props);

  let { keyboardProps } = useKeyboard({
    onKeyDown: (e) => e.key === "Escape" && state.close(),
    onKeyUp: (e) => (e) => e.key === "Escape" && state.close(),
  });

  // Get props for the menu trigger and menu elements
  let ref = React.useRef();
  let { menuTriggerProps, menuProps } = useMenuTrigger({}, state, ref);

  // Get props for the button based on the trigger props from useMenuTrigger
  let { buttonProps } = useButton(menuTriggerProps, ref);

  const {
    app: { isPrintServiceUnavailable },
  }: StoreType = useSelector(GET_APP_STATE, shallowEqual);

  useEffect(() => {
    if (isPrintServiceUnavailable) {
      state.close();
    }
  }, [state, isPrintServiceUnavailable]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <PrintButton
        variant="secondary-white"
        width={[40, 48]}
        height={[40, 48]}
        paddingX={0}
        aria-label="Print"
        aria-labelledby="print btn"
        {...buttonProps}
        {...keyboardProps}
        ref={ref}
      >
        <PrintSVG display="inline-flex;" fill="white" />
      </PrintButton>
      {state.isOpen && (
        <MenuPopup
          {...props}
          domProps={menuProps}
          autoFocus={state.focusStrategy}
          onClose={() => state.close()}
        />
      )}
    </div>
  );
}

const Ul = styled.ul`
  li:first-of-type {
    margin-top: -3px !important;
  }
`;

function MenuPopup(props) {
  // Create menu state based on the incoming props
  let state = useTreeState({ ...props, selectionMode: "none" });

  // Get props for the menu element
  let ref = React.useRef();
  let { menuProps } = useMenu(props, state, ref);

  // Handle events that should cause the menu to close,
  // e.g. blur, clicking outside, or pressing the escape key.
  let overlayRef = React.useRef();
  let { overlayProps } = useOverlay(
    {
      onClose: props.onClose,
      shouldCloseOnBlur: true,
      isOpen: true,
      isDismissable: true,
    },
    overlayRef
  );

  // Wrap in <FocusScope> so that focus is restored back to the
  // trigger when the menu is closed. In addition, add hidden
  // <DismissButton> components at the start and end of the list
  // to allow screen reader users to dismiss the popup easily.
  return (
    <FocusScope restoreFocus>
      <div {...overlayProps} ref={overlayRef}>
        <DismissButton onDismiss={props.onClose} />
        <Ul
          {...mergeProps(menuProps, props.domProps)}
          ref={ref}
          style={{
            position: "absolute",
            minWidth: "320px",
            margin: "8px 0 0 0",
            listStyle: "none",
            background: "white",
            borderRadius: "8px",
            right: "0px",
            padding: "25px 0px",
            boxShadow: "0 2px 24px 0 rgba(53, 53, 53, 0.5)",
          }}
        >
          <Box display="flex" justifyContent="flex-end" mr={24} height="auto">
            <button
              onClick={props.onClose}
              style={{
                padding: "0",
                border: "0",
                height: "18px",
                backgroundColor: "white",
              }}
            >
              <TimesSVG width="18px" height="18px" fill="#070b16" />
            </button>
          </Box>
          {[...state.collection].map((section, index) => {
            return [...section.childNodes].map((item) => (
              <MenuItem
                key={item.key}
                item={item}
                state={state}
                onAction={props.onAction}
                onClose={props.onClose}
                index={index}
              />
            ));
          })}
        </Ul>
        <DismissButton onDismiss={props.onClose} />
      </div>
    </FocusScope>
  );
}

function MenuItem({ item, state, onAction, onClose, index }) {
  // Get props for the menu item element
  let ref = React.useRef();
  let { menuItemProps } = useMenuItem(
    {
      key: item.key,
      isDisabled: item.isDisabled,
      onAction,
      onClose,
      closeOnSelect: false,
    },
    state,
    ref
  );

  // Handle focus events so we can apply highlighted
  // style to the focused menu item
  let [isFocused, setFocused] = React.useState(false);
  let { focusProps } = useFocus({ onFocusChange: setFocused });

  return (
    <li
      {...mergeProps(menuItemProps, focusProps)}
      ref={ref}
      style={{
        background: isFocused ? "white" : "transparent",
        color: isFocused ? "white" : "black",
        padding: "2px 5px",
        outline: "none",
        marginBottom: item.key.includes("https") ? "11px" : "0px",
        marginTop: "9px",
      }}
    >
      {item.rendered}
    </li>
  );
}

export default PrintMenuButton;
